import { Component } from 'react';
import { Accordion, Alert, Button, Form } from "react-bootstrap";
import GameData from "../mobx/GameData";
import RomData from '../data/RomData';
import { observe } from 'mobx';
import ApplicationState from "../mobx/ApplicationState";
import { Col, Row, Container } from "react-bootstrap";

import './SfxPage.scss';
import { coerceUnknownFields } from '../data/BaseGame';

import { nsf2data } from 'famitone2-tools-emscripten'

const expectedSfx = [
    "Undo action",
    "Pick up key",
    "Level complete",
    "Menu item swap",
    "Menu open",
    "Menu close",
    "Crate falls into hole",
    "Crate moved",
    "Switch Flipped"
]

export default class SfxPage extends Component {

    thisId = null;
    gameDataCleanup = null;
    BuiltInSongs = [];

    // eslint-disable-next-line no-useless-constructor
    constructor(props) {
        super(props);
        const game = coerceUnknownFields(GameData.currentGame);

        this.state = {
            loading: true,
            usingBuiltInSfx: !!game.usingBuiltInSfx,
            sfxData: game.sfxData ?? [],
        }
    }

    async componentDidMount() {
        this.gameDataCleanup = observe(GameData, () => { 
            this.forceUpdate()
        });

        this.setState({loading: false});
    }

    componentWillUnmount() {
        if (this.gameDataCleanup) {
            this.gameDataCleanup();
        }
    }

    async customEnabled() {
        await new Promise(resolve => this.setState({
            usingBuiltInSfx: document.getElementById('use-builtin-sfx-yes').checked,
        }, resolve)); 
        ApplicationState.setCurrentPageUnsaved();
    }

    fieldChanged(name) {
        this.setState({[name]: parseInt(document.getElementById(name).value, 10)});
        this.forceUpdate();
        ApplicationState.setCurrentPageUnsaved();
    }
    
    async fileUpload(eType, event) {
        let field, maxlength;
        switch (eType) {
            case 'binFile': field = 'sfxData'; maxlength = 1024; break;
            default: throw new Error('Unknown eType: ' + eType);
        }
        const file = document.getElementById(eType).files[0];
        let nsfData = new Uint8Array(await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result)
            reader.onerror = (e) => reject(e);

            reader.readAsArrayBuffer(file);
        }));

        let converted
        try {
            converted = await nsf2data(nsfData, {moduleSettings: {locateFile: (path) => {
                return `${process.env.PUBLIC_URL}/emscripten-data/${path}`
            }}})
        } catch (e) {
            ApplicationState.addAlert('error', 'Failed converting file. Check to make sure your nsf is valid.')
            console.error('Something went wrong converting nsf to assembly', e)
            return
        }
        
        if (converted.size.length > maxlength) {
            ApplicationState.addAlert('danger', 'Unable to load file - it must be no more than ' + maxlength + ' bytes in size! Check the FAQ at the bottom for help.');
            return;
        }

        if (converted.effects.length < 8) {
            ApplicationState.addAlert('warning', 'This file has less than 8 sound effects in it. This could lead to undefined behavior! Continue at your own risk.');
        }

        if (converted.effects.length > 8) {
            ApplicationState.addAlert('info', 'This file has more than 8 sound effects in it. The extra sound effects will be inaccessible without code modificaations.');
        }

        const data = this.binify(converted.data);
        if (data.length !== converted.size) {
            console.warn('Conversion may have failed - lengths do not match!', {converted, output: data})
        }

        await new Promise(resolve => this.setState({[field]: Array.from(data)}, resolve));
        this.forceUpdate();


        ApplicationState.setCurrentPageUnsaved();
    }


    async saveChanges(event) {
        if (event && event.preventDefault) {
            event.preventDefault();
        }

        if (!this.state.usingBuiltInSfx && this.state.sfxData.length === 0) {
            ApplicationState.addAlert('warning', 'No custom sound effect data was loaded, so this game cannot be saved. Please upload a file, or switch to built-in sound effects.');
            return;
        }

        let updatedGame;
        if (this.state.builtInSfx) {
            updatedGame = {
                ...GameData.currentGame,
                usingBuiltInSfx: true,
                sfxData: [],
            };
        } else {
            updatedGame = {
                ...GameData.currentGame,
                usingBuiltInSfx: true,
                sfxData: this.state.sfxData
            };
        }
        GameData.replaceCurrentGame(updatedGame);

        // SUCCESS! Share the update!
        ApplicationState.addAlert('success', 'Sound effects updated successfully.');
        ApplicationState.unsetCurrentPageUnsaved();
        
    }

/*

Sound data is currently in KERNEL!! Which may make sense to keep for now. 
828 bytes total with ntsc and pal. Let's limit to 1024. Add an FAQ item saying pick either ntsc or pal
if you're struggling to make it fit. Oh, and a setting

Give the built-in sounds as a downloadable example.
*/

    binify(asmRaw) {
        const asmData = asmRaw.split('\n').map(l => l.trim())

        // First pass, convert all raw binary to bin arrays
        const data1 = []
        asmData.forEach(asm => {
            if (asm.endsWith(':')) {
                data1.push({key: 'label', val: asm.substring(0, asm.length - 1)})
            } else if (asm.startsWith('.word')) {
                data1.push({key: 'word', val: asm.substring(6)})
            } else if (asm.startsWith('.byte')) {
                const binData = asm.substring(6).split(',').map(a => parseInt(a.substring(1), 16))
                data1.push({key: 'bytes', val: binData})
            } else if (asm.length === 0 || asm.startsWith(';')) {
                // Do nothing
            } else {
                console.warn('Unknown line encountered during asm conversion:', asm)
            }
        })

        // Second pass, get relative values for all labels
        let currByte = 0;
        data1.forEach(obj => {
            const {key, val} = obj;
            if (key === 'label') {
                obj.offset = currByte
            } else if (key === 'word') {
                currByte += 2;
            } else if (key === 'bytes') {
                currByte += val.length
            }
        })
        const data2 = [];
        const romOffsetFull = RomData.patches.find(p => p.name === 'sfxData').address,
        // NOTE: I am not sure why this works. I am honestly probably cheating to get this value right.
            romOffset = (romOffsetFull & 0xffff) - 0x10
        data1.forEach(obj => {
            const {key, val} = obj;
            if (key === 'word') {
                const wordAddr = data1.find(w => w.key === 'label' && w.val === val).offset,
                    fullAddr = wordAddr + romOffset
                data2.push(fullAddr & 0xff)
                data2.push(fullAddr >> 8)
            } else if (key === 'bytes') {
                data2.push(...val)
            }
        })
        return data2
    }

    render() {
        const game = coerceUnknownFields(GameData.currentGame)
        let mainForm
        let bottomStuff
        if (this.state.loading || ApplicationState.emulatorIsVisible) { return <></> }
        if (this.state.usingBuiltInSfx) {
            mainForm = <>
                <h3 className="mb-2 mt-3">Using built-in sound effects</h3>
                {(game.usingBuiltInSfx !== this.state.usingBuiltInSfx) ? (
                    <Alert variant="warning"><strong>Warning:</strong> Going back to built-in sound effects after using custom sound effects will erase your custom sound effects!</Alert>
                ) : ''}
                <p>Nothing to configure. Select custom sound effects to replace the built-in sound effects.</p>
            </>

            bottomStuff = <>
            </>
        } else {
            mainForm = <>
                <h3 className="mb-2 mt-3">Custom Sound Effects</h3>
                <p>You can upload a custom sound effect file below. Sound effects should be created as separate songs in famitracker, and exported as an nsf. See the FAQ at the bottom for details, and links to tools you can use!</p>
                <p>
                    Your sound effects must be in separate songs in famitracker, and exported as an NSF file. Additionally, 
                    the sounds must appear in the order below, in order for the game to use them as expected. The names in
                    Famitracker are ignored.
                </p>
                <ol>
                    {expectedSfx.map(a => (<li key={a}>{a}</li>))}
                </ol>
                <Container className="upload-form">
                    <Row>
                        <Col md={12} sm={12}>
                            <Form.Group controlId="binFile" className="mb-3">
                                <Form.Label>Sound Effect Data (famitracker nes .nsf file, must convert to under '1kb')</Form.Label>
                                <Form.Control type="file" name="binFile" accept=".nsf" onChange={event => this.fileUpload('binFile', event)} />
                            </Form.Group>
                            <div>
                                {this.state.sfxData.length > 0 ? 'Sound effect data loaded (' + this.state.sfxData.length + ' bytes)' : 'Sound effect data not loaded'}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>

            bottomStuff = <>
                <Accordion className="mt-3" defaultActiveKey="0">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>How can I create sound effects?</Accordion.Header>
                        <Accordion.Body>
                            <p>
                                You can create sound effects compatible with this engine using a tool called{' '}
                                <a href="https://web.archive.org/web/20230302081117/http://famitracker.com/">Famitracker</a>. (The original version 0.4.6 is the only one officially supported. Forks could work but are unsupported.)
                            </p>

                            <p>
                                You should have at least 8 songs in the file, which will be picked up and used by the engine automatically. These songs must be in the order shown above.
                            </p>

                            <p>
                                You can download the default sound effects <a target="_blank" rel="noreferrer" href={`${process.env.PUBLIC_URL}/examples/sfx.ftm`}>here</a>.
                            </p>

                            <p>
                                Almost features of famitracker are supported, except for DPCM and sweeps.
                            </p>
                            <p>
                                Each effect should be finished with silence - meaning the <code>C00</code> command must be in each used column
                                in the last row of your sound effect.
                            </p>
                            <p>
                                To export in a format this tool understands, go to the "File" menu and choose the "Create NSF" option. You can leave all fields blank, and
                                leave the "Type of File" field as "NSF - Nintendo Sound File". 
                                Hit export and upload the resulting bin file in the form above.
                            </p>
                            <p>
                                Be warned that if you have fewer than 8 sound effects, the game may crash. If you are not using a sound effect, replace
                                the effect with silence (<code>C00</code> command in the first column)
                            </p>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </>
        }
        return (
            <Container className="sfx-page">
                <Row>
                    <Col>
                        <h1>Sound Effects</h1>

                        <p>Control the sound effects your game uses here.</p>

                        <Form noValidate ref={this.form} onSubmit={event => this.saveChanges(event)}>
                            <Form.Group className="mb-3">
                                <Form.Check type="radio" id="use-builtin-sfx-yes" label="Use Built-in sound effects" defaultChecked={this.state.usingBuiltInSfx} name="usingBuiltInSfx" onChange={() => this.customEnabled()} />
                                <Form.Check type="radio" id="use-builtin-sfx-no" label="Use Custom sound effects" defaultChecked={!this.state.usingBuiltInSfx} name="usingBuiltInSfx" onChange={() => this.customEnabled()} />

                            </Form.Group>

                            {mainForm}
                            
                            <div className="button-footer-bar">
                                <Button variant="primary" type="submit">Save Changes</Button>
                            </div>
                        </Form>

                        {bottomStuff}
                    </Col>
                </Row>

            </Container>
        );
    }
}